import React, { useMemo, useEffect, useCallback } from "react";

import { AuthContext } from "../auth-context";

import type { AuthState } from "../../types";
import { ROLE_KEY, STORAGE_KEY } from "./constant";
import api, { endpoints } from "../../../api/api";
import { useSetState } from "../../../hooks/use-set-state";
import { setSession } from "./utils";

// ----------------------------------------------------------------------

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
  });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);
      const roleToken = localStorage.getItem(ROLE_KEY);

      if (accessToken && roleToken?.toLowerCase() !== "admin") {
        setSession(accessToken);
        setSession(roleToken, ROLE_KEY);

        const res = await api.get(endpoints.auth.me);
        const user = res.data;

        setState({
          user: { ...user, accessToken, role: roleToken },
          loading: false,
        });
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            role: state.user?.role ?? "admin",
          }
        : null,
      checkUserSession,
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
    }),
    [checkUserSession, state.user, status]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
